import React from 'react'
import { Link, useNavigate } from 'react-router'
import { ThemeToggle } from '../../components/common/ThemeToggle'
import { useSecretGesture } from '../../hooks/useSecretGesture'
import { ServerWakeGuard } from '../../components/auth/ServerWakeGuard'

export default function AuthLayout({
  children,
}: {
  children: React.ReactNode
}) {
  // Atalho escondido: 5 cliques no header/logo em <2s navegam para o painel
  // de controle do scheduler. Não há feedback visual — gesto invisível para
  // quem não conhece. Único caminho prático no mobile pra acordar o sistema
  // quando o autor está logado fora do horário pelo banner de manutenção.
  const navigate = useNavigate()
  const secretGesture = useSecretGesture({
    count: 5,
    windowMs: 2000,
    onTrigger: () => navigate('/_/scheduler'),
  })

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
      {/* Desktop Layout */}
      <div className="hidden lg:flex h-screen">
        {/* Left Side - Form */}
        <div className="flex flex-col justify-center w-1/2 px-8 xl:px-16">
          <div className="w-full max-w-lg mx-auto">
            {/* Form Card */}
            <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
              {/* Card Header with Gradient — também é o alvo do atalho escondido (5 cliques) */}
              <div
                className="bg-gradient-to-r from-brand-500 to-brand-600 dark:from-brand-600 dark:to-brand-700 px-8 py-6 text-center select-none"
                onClick={secretGesture}
              >
                <h2 className="text-2xl font-bold text-white">
                  Bem-vindo de volta
                </h2>
                <p className="text-brand-50 dark:text-brand-100 text-sm mt-1">
                  Acesse sua conta para continuar
                </p>
              </div>

              {/* Form Content */}
              <div className="px-8 py-8">{children}</div>
            </div>
          </div>
        </div>

        {/* Right Side - Wallpaper */}
        <div className="relative w-1/2 h-full">
          <img
            src="/images/auth/auth-wallpaper-tech.jpg"
            alt="MDi Optimus Tech Background"
            className="w-full h-full"
          />
        </div>
      </div>

      {/* Mobile Layout */}
      <div className="lg:hidden min-h-screen flex flex-col p-6">
        {/* Mobile Logo */}
        <div className="flex justify-center mb-6 pt-6">
          <Link to="/" className="inline-block">
            <img
              src="/images/auth/auth-logo-mobile.png"
              alt="MDi Optimus"
              className="h-24 w-auto object-contain drop-shadow-xl"
            />
          </Link>
        </div>

        {/* Mobile Form Card */}
        <div className="flex-1 flex items-center justify-center pb-12">
          <div className="w-full max-w-md">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl overflow-hidden border border-gray-200 dark:border-gray-700">
              {/* Mobile Header — também é o alvo do atalho escondido (5 taps) */}
              <div
                className="bg-gradient-to-r from-brand-500 to-brand-600 dark:from-brand-600 dark:to-brand-700 px-6 py-5 text-center select-none"
                onClick={secretGesture}
              >
                <h2 className="text-xl font-bold text-white">
                  Bem-vindo de volta
                </h2>
                <p className="text-brand-50 dark:text-brand-100 text-xs mt-1">
                  Acesse sua conta para continuar
                </p>
              </div>

              {/* Mobile Form */}
              <div className="p-6">{children}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Theme Toggle - Fixed Position */}
      <div className="fixed z-50 bottom-6 right-6">
        <ThemeToggle variant="prominent" />
      </div>

      {/* Popup de wake do servidor — montado uma unica vez (fora do
          {children} que o layout duplica em desktop/mobile). */}
      <ServerWakeGuard />
    </div>
  )
}
